import React, { useRef } from 'react';
import cn from 'classnames';

import { isHorizontal } from '@Components/D3Tree/utils';
import SvgUseAsButton from './SvgUseAsButton';

import './LinkNode.theme.scss';

export const LINK_NODE_HORIZONTAL_HEIGHT = 142;
export const LINK_NODE_HORIZONTAL_WIDTH = 32;
export const LINK_NODE_VERTICAL_HEIGHT = 32;
export const LINK_NODE_VERTICAL_WIDTH = 142;

const LAYOUT_HORIZONTAL = {
  height: LINK_NODE_HORIZONTAL_HEIGHT,
  width: LINK_NODE_HORIZONTAL_WIDTH,
  beforeText: {
    x: 16,
    y: (LINK_NODE_HORIZONTAL_HEIGHT - 56) / 2,
    textAnchor: 'middle',
  },
  moreButton: {
    height: 32,
    width: 32,
    x: 0,
    y: (LINK_NODE_HORIZONTAL_HEIGHT - 32) / 2,
    dotMargin: 5,
  },
  stepText: {
    x: 16,
    y: LINK_NODE_HORIZONTAL_HEIGHT - 32,
    textAnchor: 'middle',
  },
  stepNumber: {
    x: 16,
    y: LINK_NODE_HORIZONTAL_HEIGHT - 8,
    textAnchor: 'middle',
  },
  stepRect: {
    x: 16 - 10, // -10 from stepNumber.x
    y: LINK_NODE_HORIZONTAL_HEIGHT - 8 - 14, // -14 from stepNumber.y
    baseWidth: 18,
    widthCharsMultiplier: 1.75,
    height: 18,
  },
};

const LAYOUT_VERTICAL = {
  height: LINK_NODE_VERTICAL_HEIGHT,
  width: LINK_NODE_VERTICAL_WIDTH,
  beforeText: {
    x: (LINK_NODE_VERTICAL_WIDTH - 32) / 2 - 8,
    y: (LINK_NODE_VERTICAL_HEIGHT + 8) / 2,
    textAnchor: 'end',
  },
  moreButton: {
    height: 32,
    width: 32,
    x: (LINK_NODE_VERTICAL_WIDTH - 32) / 2,
    y: 0,
    dotMargin: 5,
  },
  stepText: {
    x: (LINK_NODE_VERTICAL_WIDTH - 32) / 2 + 32 + 8,
    y: (LINK_NODE_VERTICAL_HEIGHT + 8) / 2,
    textAnchor: 'start',
  },
  stepNumber: {
    x: (LINK_NODE_VERTICAL_WIDTH - 32) / 2 + 32 + 40,
    y: (LINK_NODE_VERTICAL_HEIGHT + 8) / 2,
    textAnchor: 'start',
  },
  stepRect: {
    x: (LINK_NODE_VERTICAL_WIDTH - 32) / 2 + 32 + 40 - 4, // -4 from stepNumber.x
    y: (LINK_NODE_VERTICAL_HEIGHT + 8) / 2 - 14, // -14 from stepNumber.y
    baseWidth: 16,
    widthCharsMultiplier: 2,
    height: 18,
  },
};

const shiftNode = (width, height) => ({
  x: -width / 2,
  y: -height / 2,
});

// eslint-disable-next-line react/prop-types
export default function LinkNode({ textAbove, step, stepData, orientation, activeStep, buttonsDisabled, onClick }) {
  const layoutConfig = isHorizontal(orientation) ? LAYOUT_HORIZONTAL : LAYOUT_VERTICAL;
  const { width, height, beforeText, stepText, stepNumber, stepRect, moreButton } = layoutConfig;
  const { x, y } = shiftNode(width, height);
  const isCurrentStep = step === activeStep;
  const iconRef = useRef(null);

  const onClickHandler = () => {
    const icon = iconRef.current;
    onClick({ step, targetRect: icon.getBoundingClientRect(), stepData });
  };

  return (
    <svg
      className={cn('rt-project-route-link-node', { 'rt-project-route-link-node--buttons-disabled': buttonsDisabled })}
      height={height}
      width={width}
      x={x}
      y={y}>
      <SvgUseAsButton
        x={0}
        y={0}
        height={height}
        width={width}
        wrapperClassName="rt-project-route-link-node__wrapper"
        onClick={onClickHandler}
        tabIndex={buttonsDisabled ? -1 : 0}>
        <text
          x={beforeText.x}
          y={beforeText.y}
          transform={beforeText.transform}
          textAnchor={beforeText.textAnchor}
          className="rt-project-route-link-node__above-text">
          {textAbove}
        </text>

        <svg
          x={moreButton.x}
          y={moreButton.y}
          height={moreButton.height}
          width={moreButton.width}
          className="rt-project-route-link-node__button">
          <rect className="rt-project-route-link-node__button-background" height={moreButton.height} width={moreButton.width} y={0} x={0} />
          <use
            ref={iconRef}
            className="rt-project-route-link-node__button-icon"
            href="#icon-dragging-bar-dots"
            height={moreButton.height - moreButton.dotMargin * 2}
            width={moreButton.width - moreButton.dotMargin * 2}
            y={moreButton.dotMargin}
            x={moreButton.dotMargin}
          />
        </svg>

        {step && (
          <>
            <text x={stepText.x} y={stepText.y} textAnchor={stepText.textAnchor}>
              Step
            </text>
            <rect
              className={cn('rt-project-route-link-node__step-rect', { current: isCurrentStep })}
              x={stepRect.x}
              y={stepRect.y}
              /* eslint-disable-next-line react/prop-types */
              width={stepRect.baseWidth + step.length * stepRect.widthCharsMultiplier}
              height={stepRect.height}
            />
            <text x={stepNumber.x} y={stepNumber.y} textAnchor={stepNumber.textAnchor}>
              {step}
            </text>
          </>
        )}
      </SvgUseAsButton>
    </svg>
  );
}
